/*
  Better Digital, Offer Audit Checklist (B4).

  Two input modes, one result shape:
    guided  the seven levers scored in the browser from field answers. A
            signed-in visitor can explicitly save the grade metadata.
    paste   the offer copy sent to the App Proxy, parsed and scored by the AI
            layer, which returns the same result shape. Account required.

  Ungated: grade, band, the one weakest lever, and how many more were found.
  Gated:   the full rubric, rendered into a container Liquid only prints for a
           logged-in customer, so the depth is never sent to a logged-out browser.
*/
(function (global) {
  'use strict';

  /* --------------------------------------------------------------- vocabulary */

  // Words that sound like value but carry none. Each one costs promise clarity.
  var HYPE = [
    'premium', 'quality', 'high-quality', 'best', 'finest', 'amazing', 'incredible',
    'revolutionary', 'innovative', 'cutting-edge', 'world-class', 'luxurious',
    'exquisite', 'curated', 'elevate', 'elevated', 'unrivalled', 'unrivaled',
    'unparalleled', 'ultimate', 'game-changing', 'next-level', 'bespoke',
    'handcrafted', 'artisan', 'seamless', 'effortless', 'perfect', 'stunning'
  ];

  // Language that names a change in the customer's life rather than a product trait.
  var OUTCOME = [
    'so you', 'without', 'stop', 'never', 'finally', 'in as little as', 'in just',
    'get', 'gets', 'keeps', 'keep', 'save', 'saves', 'cut', 'cuts', 'feel', 'feels',
    'sleep', 'sleeps', 'fits', 'lasts', 'stays', 'avoid', 'no more', 'instead of',
    'ready', 'done', 'sorted', 'covered', 'hold', 'holds', 'stays put', 'still', 'again'
  ];

  // Claims every competitor also makes, so they differentiate nothing.
  var COMMODITY = [
    'quality', 'best', 'great service', 'good service', 'customer service',
    'passion', 'passionate', 'we care', 'care about', 'value for money',
    'affordable', 'cheap', 'competitive price', 'competitive prices', 'trusted',
    'reliable', 'friendly', 'love what we do', 'attention to detail'
  ];

  var TIME_UNIT = '(?:second|minute|hour|day|week|fortnight|month|quarter|year)';
  var INVENTORY_TIME_UNIT = '(?:' + TIME_UNIT + '|night)';
  var TIME_WORDS = new RegExp(
    '\\b(?:' + TIME_UNIT + 's?|(?:\\d[\\d,.]*|a|one)\\s+nights?|' +
    'overnight|same(?:[ .-])?day|next(?:[ .-])?day)\\b',
    'i'
  );
  var PRODUCT_QUANTITY = new RegExp(
    '\\b\\d[\\d,.]*\\s*(?:[-–]\\s*)?(?:' + INVENTORY_TIME_UNIT +
    's?\\s+(?:of\\s+)?)?(?:supply|capsules?|tablets?|servings?|pieces?|' +
    'packs?|units?|bottles?|sachets?|gummies?|items?|count|ct|refills?)\\b',
    'gi'
  );
  var MEASUREMENT = /\b\d[\d,.]*\s*(?:ml|l|g|kg|mg|oz|lb|cm|mm)\b/gi;
  var INVENTORY_CONTAINER = /^\s+(?:bottles?|bags?|packs?|packets?|sachets?|tubs?|jars?|cans?|capsules?|tablets?|servings?|pieces?|units?|items?)\b/i;
  var INVENTORY_LEAD_IN = /(?:^|[.!?;:]\s*|\b(?:a|an|includes?|contains?|containing|with|plus)\s+)$/i;
  var OFFER_TEXT_MAX_LENGTH = 8000;

  /* ------------------------------------------------------------------- levers */

  var LEVERS = [
    {
      key: 'promise',
      name: 'Promise clarity',
      weight: 20,
      ask: 'Can a stranger say what they get, in one line?'
    },
    {
      key: 'specificity',
      name: 'Specificity',
      weight: 15,
      ask: 'Is the outcome concrete, or a feature list?'
    },
    {
      key: 'value',
      name: 'Value vs price',
      weight: 20,
      ask: 'Is the stack visibly worth more than the number?'
    },
    {
      key: 'risk',
      name: 'Risk reversal',
      weight: 15,
      ask: 'Who carries the risk of being wrong?'
    },
    {
      key: 'proof',
      name: 'Proof',
      weight: 15,
      ask: 'Is there evidence it works for someone like them?'
    },
    {
      key: 'differentiation',
      name: 'Differentiation',
      weight: 10,
      ask: 'Why you, and not the cheaper one?'
    },
    {
      key: 'urgency',
      name: 'Honest urgency',
      weight: 5,
      ask: 'Is there a real reason to act now?'
    }
  ];

  var BANDS = [
    { min: 80, grade: 'A', label: 'Your offer carries its own weight', tone: 'strong' },
    { min: 65, grade: 'B', label: 'Close, but one lever is soft', tone: 'strong' },
    { min: 50, grade: 'C', label: 'The price is doing too much of the work', tone: 'leaky' },
    { min: 35, grade: 'D', label: 'Discount dependent', tone: 'leaky' },
    { min: 0, grade: 'F', label: 'This is a product, not an offer yet', tone: 'broken' }
  ];

  /* -------------------------------------------------------------- small tools */

  function queryAll(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function clamp(value, low, high) {
    return Math.max(low, Math.min(high, value));
  }

  function text(value) {
    return String(value == null ? '' : value).trim();
  }

  function lower(value) {
    return text(value).toLowerCase();
  }

  function words(value) {
    var trimmed = text(value);
    return trimmed ? trimmed.split(/\s+/) : [];
  }

  function lines(value) {
    return text(value)
      .split(/\r?\n/)
      .map(function (line) {
        return line.replace(/^\s*(?:[•*-]\s+|\d+[.)]\s+)/, '').trim();
      })
      .filter(function (line) { return line.length > 0; });
  }

  function vocabularyMatches(haystack, needles) {
    var hay = ' ' + lower(haystack)
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/\s+/g, ' ') + ' ';

    return needles.filter(function (needle) {
      var term = lower(needle)
        .replace(/[^a-z0-9\s-]/g, ' ')
        .replace(/\s+/g, ' ');
      return term && hay.indexOf(' ' + term + ' ') > -1;
    });
  }

  function countMatches(haystack, needles) {
    return vocabularyMatches(haystack, needles).length;
  }

  function firstMatch(haystack, needles) {
    var matches = vocabularyMatches(haystack, needles);
    return matches.length ? matches[0] : '';
  }

  var MONEY_NUMBER_TOKEN = '(?:' +
    '\\d{1,3}(?:[ \\u00a0\\u202f]\\d{3})+(?:[.,]\\d{1,2})?' +
    '|\\d(?:[\\d.,]*\\d)?' +
    ')';
  var MONEY_NUMBER_BOUNDARY = '(?!\\d|[.,]\\d)';
  var CURRENCY_TOKEN = '(?:NZ\\$|AU\\$|US\\$|A\\$|USD|CAD|AUD|NZD|GBP|EUR|JPY|SGD|HKD|CHF|CNY|INR|SEK|NOK|DKK|\\$|£|€)';

  function currencyIdentity(value) {
    var currency = text(value).toUpperCase();
    var aliases = {
      'A$': 'AU$',
      'AU$': 'AU$',
      AUD: 'AU$',
      NZD: 'NZ$',
      USD: 'US$',
      GBP: '£',
      EUR: '€'
    };
    return aliases[currency] || currency;
  }

  // Currency placement and numeric separators are normalized once, before any
  // monetary path decides whether a value is a stack line or an asking price.
  function moneyText(value) {
    return text(value).replace(
      new RegExp('(' + MONEY_NUMBER_TOKEN + ')\\s*(' + CURRENCY_TOKEN + ')', 'gi'),
      '$2$1'
    );
  }

  function moneyNumber(value, currency) {
    var raw = text(value).replace(/\s/g, '');
    if (!raw) return 0;

    var lastComma = raw.lastIndexOf(',');
    var lastDot = raw.lastIndexOf('.');
    var normalized = raw;
    var decimalIndex = -1;
    var groupedOnly = false;

    if (lastComma > -1 && lastDot > -1) {
      decimalIndex = Math.max(lastComma, lastDot);
    } else if (lastComma > -1) {
      var commaDecimals = raw.length - lastComma - 1;
      if (commaDecimals > 0 && commaDecimals <= 2) decimalIndex = lastComma;
      else groupedOnly = true;
    } else if (lastDot > -1) {
      var dotDecimals = raw.length - lastDot - 1;
      if (raw.indexOf('.') !== lastDot) {
        if (dotDecimals > 0 && dotDecimals <= 2) decimalIndex = lastDot;
        else groupedOnly = true;
      } else if (currency === '€' && dotDecimals === 3) {
        groupedOnly = true;
      }
    }

    if (decimalIndex > -1) {
      normalized = raw.slice(0, decimalIndex).replace(/[.,]/g, '') + '.' +
        raw.slice(decimalIndex + 1).replace(/[.,]/g, '');
    } else if (groupedOnly) {
      normalized = raw.replace(/[.,]/g, '');
    }

    var parsed = parseFloat(normalized);
    return isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  function markedMoney(value) {
    var amounts = [];
    var pattern = new RegExp('(' + CURRENCY_TOKEN + ')\\s?(' + MONEY_NUMBER_TOKEN + ')', 'gi');
    var match;
    var raw = moneyText(value);
    while ((match = pattern.exec(raw)) !== null) {
      var currency = currencyIdentity(match[1]);
      var parsed = moneyNumber(match[2], currency);
      if (parsed) {
        amounts.push({ value: parsed, currency: currency });
      }
    }
    return amounts;
  }

  function multipliedLineMoney(line) {
    var raw = moneyText(line);
    var perUnit = raw.match(new RegExp(
      '\\b(\\d{1,3})\\b[^$£€\\d]{0,80}(' + CURRENCY_TOKEN + ')\\s?(' +
      MONEY_NUMBER_TOKEN +
      ')\\s*(?:each|per(?:\\s+(?:item|unit|session|consultation|call|piece))?)\\b',
      'i'
    ));
    var compact = raw.match(new RegExp(
      '\\b(\\d{1,3})\\s*(?:x|×)\\s*(' + CURRENCY_TOKEN + ')\\s?(' +
      MONEY_NUMBER_TOKEN + ')',
      'i'
    ));
    var match = perUnit || compact;
    if (!match) return { value: 0, currency: '' };

    var count = parseInt(match[1], 10);
    var currency = currencyIdentity(match[2]);
    var amount = moneyNumber(match[3], currency);
    return isFinite(count) && count > 0 && amount
      ? { value: count * amount, currency: currency }
      : { value: 0, currency: '' };
  }

  // The value of one stack line. Only a currency-marked number counts, so
  // "The 30-night blend $89" reads as $89 and not $3089.
  function lineMoney(line) {
    var amounts = markedMoney(line);
    if (!amounts.length) return { value: 0, currency: '' };
    // Take the largest marked figure: "$29 today, normally $49" is worth $49.
    var best = amounts.reduce(function (highest, amount) {
      return amount.value > highest.value ? amount : highest;
    }, { value: 0, currency: '' });
    var multiplied = multipliedLineMoney(line);
    return multiplied.value > best.value ? multiplied : best;
  }

  function lineValue(line) {
    return lineMoney(line).value;
  }

  function installmentValueOf(value, currencyHint, atStart) {
    var raw = moneyText(value);
    var anchor = atStart ? '^\\s*' : '';
    var amountFirst = '(' + CURRENCY_TOKEN + ')?\\s*(' + MONEY_NUMBER_TOKEN + ')\\s*';
    var grammars = [
      {
        pattern: new RegExp(
          anchor + amountFirst + '(?:x|×)\\s*(\\d{1,3})\\s*' +
          '(?:payments?|instalments?|installments?)\\b',
          'i'
        ),
        count: 3,
        currency: 1,
        amount: 2
      },
      {
        pattern: new RegExp(
          anchor + amountFirst +
          '(?:\\/\\s*(?:weeks?|fortnights?|months?)|' +
          'per\\s+(?:week|fortnight|month|payment|instalment|installment)|' +
          'weekly|fortnightly|monthly|each)\\s*' +
          '(?:for|x|×)\\s*(\\d{1,3})\\s*' +
          '(?:weeks?|fortnights?|months?|payments?|instalments?|installments?)',
          'i'
        ),
        count: 3,
        currency: 1,
        amount: 2
      },
      {
        pattern: new RegExp(
          anchor + '\\b(\\d{1,3})\\s*(?:x|×)\\s*(' +
          CURRENCY_TOKEN + ')\\s*(' + MONEY_NUMBER_TOKEN + ')',
          'i'
        ),
        count: 1,
        currency: 2,
        amount: 3
      },
      {
        pattern: new RegExp(
          anchor + '\\b(\\d{1,3})\\s*(?:(?:weekly|fortnightly|monthly)\\s+)?' +
          '(?:payments?|instalments?|installments?)\\s*(?:of|at|x|×)?\\s*(' +
          CURRENCY_TOKEN + ')?\\s*(' + MONEY_NUMBER_TOKEN + ')',
          'i'
        ),
        count: 1,
        currency: 2,
        amount: 3
      }
    ];

    for (var i = 0; i < grammars.length; i += 1) {
      var grammar = grammars[i];
      var installment = raw.match(grammar.pattern);
      if (!installment) continue;

      var count = parseInt(installment[grammar.count], 10);
      var currency = currencyIdentity(installment[grammar.currency]) ||
        currencyIdentity(currencyHint);
      var amount = moneyNumber(installment[grammar.amount], currency);
      if (isFinite(count) && count > 0 && amount) return count * amount;
    }

    return 0;
  }

  function immediatePriceValueOf(value, currencyHint, allowUnmarkedContinuation) {
    var raw = moneyText(value);
    var hint = currencyIdentity(currencyHint);
    var installments = installmentValueOf(raw, hint, true);
    if (installments) return installments;

    var marked = raw.match(new RegExp(
      '^\\s*(' + CURRENCY_TOKEN + ')\\s?(' + MONEY_NUMBER_TOKEN + ')' +
      MONEY_NUMBER_BOUNDARY,
      'i'
    ));
    if (marked) return moneyNumber(marked[2], currencyIdentity(marked[1]) || hint);

    var unmarked = raw.match(new RegExp(
      '^\\s*(' + MONEY_NUMBER_TOKEN + ')' + MONEY_NUMBER_BOUNDARY +
      '\\s*[.!?]?\\s*$',
      'i'
    ));
    if (!unmarked && allowUnmarkedContinuation) {
      unmarked = raw.match(new RegExp(
        '^\\s*(' + MONEY_NUMBER_TOKEN + ')' + MONEY_NUMBER_BOUNDARY +
        '(?=\\s*(?:[,:;()]|[—–-](?:\\s|$)))',
        'i'
      ));
    }
    return unmarked ? moneyNumber(unmarked[1], hint) : 0;
  }

  function currentPriceValueOf(value, currencyHint) {
    var raw = moneyText(value);
    var qualifiers =
      /\b(?:now|today|sale(?:\s+price)?|current(?:\s+price)?|only)\b/gi;
    var qualifier;

    while ((qualifier = qualifiers.exec(raw)) !== null) {
      var remainder = raw.slice(qualifier.index + qualifier[0].length)
        .replace(/^\s+/, '');
      var connector = remainder.match(
        /^(?:(?:is|at|for)\b|[:,]|[—–-])\s*/i
      );
      if (connector) remainder = remainder.slice(connector[0].length);

      var hasExplicitPriceIntent = /\bprice\b/i.test(qualifier[0]);
      var current = immediatePriceValueOf(
        remainder,
        currencyHint,
        hasExplicitPriceIntent
      );
      if (current) return current;
    }

    return 0;
  }

  // Asking prices and stack values have different semantics. A stack line uses
  // its largest marked value, while compare-at copy should use the current price.
  function priceValueOf(value, currencyHint) {
    var raw = moneyText(value);
    var hint = currencyIdentity(currencyHint);
    var current = currentPriceValueOf(raw, hint);
    if (current) return current;
    var installments = installmentValueOf(raw, hint);
    if (installments) return installments;
    var marked = markedMoney(raw);
    if (marked.length) return marked[0].value;
    var first = raw.match(new RegExp(MONEY_NUMBER_TOKEN));
    if (!first) return 0;
    return moneyNumber(first[0], hint);
  }

  function currencyContextFor(includeLines, price) {
    var currencies = [];
    includeLines.concat([price]).forEach(function (value) {
      markedMoney(value).forEach(function (amount) {
        if (currencies.indexOf(amount.currency) === -1) currencies.push(amount.currency);
      });
    });
    return {
      currency: currencies.length === 1 ? currencies[0] : '',
      consistent: currencies.length <= 1
    };
  }

  function escapeHtml(value) {
    return text(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function bandFor(score) {
    for (var i = 0; i < BANDS.length; i += 1) {
      if (score >= BANDS[i].min) return BANDS[i];
    }
    return BANDS[BANDS.length - 1];
  }

  var RUBRIC_STRONG_MIN = 80;
  var RUBRIC_TIGHTEN_MIN = 60;

  function toneFor(score) {
    if (score >= RUBRIC_STRONG_MIN) return 'strong';
    if (score >= RUBRIC_TIGHTEN_MIN) return 'leaky';
    return 'broken';
  }

  function rubricStateFor(tone) {
    if (tone === 'strong') return 'Working well';
    if (tone === 'leaky') return 'Worth tightening';
    return 'Needs attention';
  }

  function track(eventName, data) {
    try {
      var payload = { event: eventName };
      if (data) {
        Object.keys(data).forEach(function (key) { payload[key] = data[key]; });
      }
      global.dataLayer = global.dataLayer || [];
      global.dataLayer.push(payload);
      if (global.document && typeof global.CustomEvent === 'function') {
        global.document.dispatchEvent(new global.CustomEvent('bd:offer-audit', { detail: payload }));
      }
    } catch (_error) { /* analytics must never break the tool */ }
  }

  /* ------------------------------------------------------------ lever scoring */

  function scorePromise(promise) {
    var count = words(promise).length;
    if (!count) return { score: 0, note: 'No promise given yet.' };

    // One line, not a paragraph and not three words.
    var length;
    if (count >= 6 && count <= 18) length = 30;
    else if (count >= 4 && count <= 24) length = 20;
    else if (count <= 30) length = 10;
    else length = 4;

    var outcome = countMatches(promise, OUTCOME) > 0 ? 35 : 0;

    var hype = countMatches(promise, HYPE);
    var clean = clamp(35 - (hype * 14), 0, 35);

    var score = clamp(Math.round(length + outcome + clean), 0, 100);
    var note;
    if (hype > 0) {
      var found = firstMatch(promise, HYPE);
      note = found
        ? 'The promise leans on "' + found + '", which every competitor also claims.'
        : 'The promise leans on words every competitor also claims.';
    } else if (!outcome) {
      note = 'The promise describes the product, not what changes for the buyer.';
    } else if (count > 24) {
      note = 'It takes ' + count + ' words to say. A stranger cannot repeat that back.';
    } else {
      note = 'A stranger could repeat this back after reading it once.';
    }
    return { score: score, note: note };
  }

  function scoreSpecificity(promise, includeLines) {
    var joined = text(promise) + ' ' + includeLines.join(' ');
    if (!text(joined)) return { score: 0, note: 'Nothing specific to read yet.' };

    // Stack prices belong to value scoring. Only a measurable outcome promise
    // can earn the number and timeframe portions of specificity.
    var measurablePromise = stripInventoryQuantities(promise);
    var hasNumber = /\d/.test(measurablePromise) ? 40 : 0;
    var hasTime = TIME_WORDS.test(measurablePromise) ? 20 : 0;
    var addressesBuyer = /\byou(r)?\b/i.test(joined) ? 20 : 0;

    var meaty = includeLines.filter(function (line) { return words(line).length >= 3; }).length;
    var detail = includeLines.length ? clamp(Math.round((meaty / includeLines.length) * 20), 0, 20) : 0;

    var score = clamp(hasNumber + hasTime + addressesBuyer + detail, 0, 100);
    var note;
    if (hasNumber) {
      note = 'There are real numbers in here, which is what makes a claim believable.';
    } else if (hasTime) {
      note = 'There is a real timeframe in here, which makes the outcome easier to picture.';
    } else {
      note = 'Nothing is measurable. Add a number or a timeframe and the same claim starts landing.';
    }
    return { score: score, note: note };
  }

  function stripInventoryQuantities(value) {
    return text(value)
      .replace(PRODUCT_QUANTITY, ' ')
      .replace(MEASUREMENT, function (measurement, offset, source) {
        var before = source.slice(0, offset);
        var after = source.slice(offset + measurement.length);
        var introducesNounQuantity = INVENTORY_LEAD_IN.test(before) &&
          /^\s+of\s+\S/i.test(after);

        return INVENTORY_CONTAINER.test(after) || introducesNounQuantity
          ? ' '
          : measurement;
      });
  }

  function scoreValue(includeLines, price) {
    var count = includeLines.length;
    var stackValue = includeLines.reduce(function (total, line) { return total + lineValue(line); }, 0);
    var priced = stackValue > 0;
    var currencyContext = currencyContextFor(includeLines, price);
    var priceValue = priceValueOf(price, currencyContext.currency);
    var comparable = priced && priceValue > 0 && currencyContext.consistent;
    var currency = currencyContext.currency;
    var ratio = comparable ? stackValue / priceValue : 0;

    var score;
    var note;

    if (comparable) {
      if (ratio >= 3) score = 95;
      else if (ratio >= 2) score = 85;
      else if (ratio >= 1.5) score = 70;
      else if (ratio >= 1) score = 55;
      else score = 30;
      note = 'Your stack shows ' + formatMoney(stackValue, currency) + ' of value against a ' + formatMoney(priceValue, currency) +
        ' price, about ' + (Math.round(ratio * 10) / 10) + 'x. ' +
        (ratio >= 2
          ? 'The buyer\'s maths tips before they reach the number.'
          : 'That is not a wide enough gap to stop the price being the story.');
    } else {
      if (count === 0) score = 10;
      else if (count === 1) score = 30;
      else if (count === 2) score = 50;
      else if (count === 3) score = 68;
      else if (count === 4) score = 80;
      else score = 88;

      if (!currencyContext.consistent) {
        note = 'The stack mixes currencies. Put every value and the asking price in one currency before comparing them.';
      } else if (!count) {
        note = 'There is no stack, just a product at a price. That is the offer asking the price to do all the work.';
      } else if (priced) {
        note = 'Your stack is worth ' + formatMoney(stackValue, currency) + ', but there is no price to weigh it against. Add the price and we can show you the gap the buyer sees.';
      } else {
        note = 'You have ' + count + ' piece' + (count === 1 ? '' : 's') + ' in the stack, but none are priced. Put a value on each and the stack starts doing the selling.';
      }
      // Without both sides of the comparison there is no value-to-price ratio,
      // so the lever cannot pass however many unmeasured pieces are listed.
      score = Math.min(clamp(score - 8, 0, 100), 60);
    }

    return {
      score: clamp(Math.round(score), 0, 100),
      note: note,
      stack: {
        value: stackValue,
        price: priceValue,
        ratio: ratio,
        priced: priced,
        items: count,
        currency: currency,
        comparable: comparable
      }
    };
  }

  var RISK_SCORES = { none: 10, returns: 45, refund: 80, results: 100 };
  var RISK_NOTES = {
    none: 'The buyer carries all the risk of being wrong. That is exactly the moment a discount starts looking necessary.',
    returns: 'A standard returns policy is the legal minimum, not a risk reversal. It reassures nobody.',
    refund: 'A no-questions refund does real work here. Say it where they see the price, not in a footer link.',
    results: 'An outcome guarantee is the strongest form of this lever. Make sure it is loud and specific.'
  };

  var PROOF_SCORES = { none: 5, few: 40, many: 65, named: 90, both: 100 };
  var PROOF_NOTES = {
    none: 'There is no evidence it works for anyone, so every claim rests on your word alone.',
    few: 'A handful of reviews reads as "new", not as "proven". Get more, and get them specific.',
    many: 'Volume is good. Now surface the ones from people your buyer recognises as themselves.',
    named: 'Named results are the strongest proof you can show. Put one above the fold, not on a reviews tab.',
    both: 'Volume plus named results is the combination that actually closes the belief gap.'
  };

  var URGENCY_SCORES = { none: 40, fake: 10, deadline: 90, scarcity: 85 };
  var URGENCY_NOTES = {
    none: 'No urgency is honest, and honest beats invented. Worth adding only if you have a real deadline.',
    fake: 'A permanent countdown or an always-on sale teaches your best customers to wait. It costs more than it earns.',
    deadline: 'A real deadline is the only urgency worth using. Keep it true and people keep believing you.',
    scarcity: 'Genuine scarcity works, as long as the limit is real and you honour it when it runs out.'
  };

  function scoreDifferentiation(why) {
    var count = words(why).length;
    if (!count) return { score: 0, note: 'Nothing here yet, so the only thing left to compare on is price.' };

    var substance = count >= 6 ? 40 : 20;
    var commodity = countMatches(why, COMMODITY);
    var distinct = clamp(30 - (commodity * 15), 0, 30);

    var raw = text(why);
    var checkable = /\d/.test(raw) || /\b(only|unlike|first|patent|certified|tested|made in|grown|sourced)\b/i.test(raw) ? 30 : 0;

    var score = clamp(substance + distinct + checkable, 0, 100);
    var note;
    if (commodity > 0) {
      note = 'This is a claim your competitors make word for word. It differentiates nothing.';
    } else if (!checkable) {
      note = 'Nothing here can be checked. Give the buyer a fact they could verify and it becomes a reason.';
    } else {
      note = 'There is something checkable here, which is what makes a reason a reason.';
    }
    return { score: score, note: note };
  }

  function formatMoney(value, currency) {
    var label = currency || '';
    var spacer = /^[A-Z]{3}$/.test(label) ? ' ' : '';
    var amount = Number(value) || 0;
    var hasCents = Math.abs(amount - Math.round(amount)) > 0.000001;
    return label + spacer + amount.toLocaleString('en-NZ', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2
    });
  }

  /* ------------------------------------------------------------ the aggregate */

  function weightsFrom(source) {
    var weights = {};
    LEVERS.forEach(function (lever) {
      var raw = source && source[lever.key];
      var parsed = parseFloat(raw);
      weights[lever.key] = isFinite(parsed) && parsed >= 0 ? parsed : lever.weight;
    });
    return weights;
  }

  function assemble(scored, weights, source, notes) {
    var totalWeight = 0;
    var totalScore = 0;

    var levers = LEVERS.map(function (lever) {
      var entry = scored[lever.key] || { score: 0, note: '' };
      var weight = weights[lever.key];
      totalWeight += weight;
      totalScore += entry.score * weight;
      // A note read off the visitor's actual copy beats the rule-based one.
      var supplied = notes && notes[lever.key];
      return {
        key: lever.key,
        name: lever.name,
        ask: lever.ask,
        score: clamp(Math.round(entry.score), 0, 100),
        weight: weight,
        note: supplied || entry.note
      };
    });

    var overall = totalWeight ? Math.round(totalScore / totalWeight) : 0;
    var band = bandFor(overall);

    // Rank by how much each lever is costing: a heavy lever at 60 outranks a light one at 20.
    var ranked = levers.slice().sort(function (a, b) {
      return ((100 - b.score) * b.weight) - ((100 - a.score) * a.weight);
    });

    var weakest = ranked[0];
    var alsoNotStrong = ranked.slice(1).filter(function (lever) {
      return lever.score < RUBRIC_STRONG_MIN;
    });

    return {
      source: source,
      score: overall,
      grade: band.grade,
      band: band.tone,
      bandLabel: band.label,
      weakest: weakest,
      moreCount: alsoNotStrong.length,
      levers: levers,
      stack: scored.value && scored.value.stack ? scored.value.stack : null
    };
  }

  /**
   * The only scorer. Both input modes end up here: typed answers go straight
   * in, and pasted copy arrives as the same seven fields after the endpoint has
   * read it. There is deliberately no second scoring path, so the two modes
   * cannot drift apart and the weights stay tunable from the theme editor.
   */
  function computeGuided(values, weights, notes, source) {
    var includeLines = lines(values.includes);
    var scored = {
      promise: scorePromise(values.promise),
      specificity: scoreSpecificity(values.promise, includeLines),
      value: scoreValue(includeLines, values.price),
      risk: { score: RISK_SCORES[values.risk] || 10, note: RISK_NOTES[values.risk] || RISK_NOTES.none },
      proof: { score: PROOF_SCORES[values.proof] || 5, note: PROOF_NOTES[values.proof] || PROOF_NOTES.none },
      differentiation: scoreDifferentiation(values.why),
      urgency: { score: URGENCY_SCORES[values.urgency] || 40, note: URGENCY_NOTES[values.urgency] || URGENCY_NOTES.none }
    };
    return assemble(scored, weights, source || 'guided', notes);
  }

  /** Validate the seven fields the endpoint extracted from pasted copy. */
  function readFields(payload) {
    var fields = payload && payload.result && payload.result.fields;
    if (!fields) return null;
    return {
      promise: text(fields.promise),
      includes: text(fields.includes),
      price: text(fields.price),
      risk: RISK_SCORES[fields.risk] !== undefined ? fields.risk : 'none',
      proof: PROOF_SCORES[fields.proof] !== undefined ? fields.proof : 'none',
      why: text(fields.why),
      urgency: URGENCY_SCORES[fields.urgency] !== undefined ? fields.urgency : 'none'
    };
  }

  function readNotes(payload) {
    var raw = (payload && payload.result && payload.result.notes) || {};
    var notes = {};
    LEVERS.forEach(function (lever) {
      var note = text(raw[lever.key]);
      if (note) notes[lever.key] = note;
    });
    return notes;
  }

  /* --------------------------------------------------------------- the widget */

  function readRubricCopy(root) {
    var copy = {};
    queryAll(root, '[data-bd-rubric-lever]').forEach(function (node) {
      var key = node.getAttribute('data-bd-rubric-lever');
      if (!key) return;
      var fix = node.querySelector('[data-bd-rubric-fix]');
      var example = node.querySelector('[data-bd-rubric-example]');
      copy[key] = {
        fix: fix ? text(fix.textContent) : '',
        example: example ? text(example.textContent) : ''
      };
    });
    return copy;
  }

  function init(root) {
    if (root.dataset.bdOfferAuditReady === 'true') return;
    root.dataset.bdOfferAuditReady = 'true';

    var weights = weightsFrom({
      promise: root.dataset.weightPromise,
      specificity: root.dataset.weightSpecificity,
      value: root.dataset.weightValue,
      risk: root.dataset.weightRisk,
      proof: root.dataset.weightProof,
      differentiation: root.dataset.weightDifferentiation,
      urgency: root.dataset.weightUrgency
    });

    var isLoggedIn = root.dataset.loggedIn === 'true';
    var endpoint = root.dataset.endpoint || '/apps/tools/offer-audit';
    var captureEndpoint = root.dataset.captureEndpoint || '';

    var field = {
      promise: root.querySelector('[data-bd-input="promise"]'),
      price: root.querySelector('[data-bd-input="price"]'),
      includes: root.querySelector('[data-bd-input="includes"]'),
      risk: root.querySelector('[data-bd-input="risk"]'),
      proof: root.querySelector('[data-bd-input="proof"]'),
      why: root.querySelector('[data-bd-input="why"]'),
      urgency: root.querySelector('[data-bd-input="urgency"]'),
      paste: root.querySelector('[data-bd-input="paste"]')
    };

    var tabGuided = root.querySelector('[data-bd-tab="guided"]');
    var tabPaste = root.querySelector('[data-bd-tab="paste"]');
    var paneGuided = root.querySelector('[data-bd-pane="guided"]');
    var panePaste = root.querySelector('[data-bd-pane="paste"]');

    var runPaste = root.querySelector('[data-bd-run-paste]');
    var saveGuided = root.querySelector('[data-bd-save-guided]');
    var toGuidedButtons = queryAll(root, '[data-bd-to-guided]');
    var registerButtons = queryAll(root, '[data-bd-register]');

    var resultCard = root.querySelector('[data-bd-result]');
    var gateCard = root.querySelector('[data-bd-gate]');
    var rubricCard = root.querySelector('[data-bd-rubric]');

    var out = {
      grade: root.querySelector('[data-bd-output="grade"]'),
      score: root.querySelector('[data-bd-output="score"]'),
      band: root.querySelector('[data-bd-output="band"]'),
      example: root.querySelector('[data-bd-output="example"]'),
      weakName: root.querySelector('[data-bd-output="weak-name"]'),
      weakNote: root.querySelector('[data-bd-output="weak-note"]'),
      more: root.querySelector('[data-bd-output="more"]'),
      stack: root.querySelector('[data-bd-output="stack"]'),
      levers: root.querySelector('[data-bd-output="levers"]'),
      status: root.querySelector('[data-bd-output="status"]'),
      cta: root.querySelector('[data-bd-output="cta"]')
    };

    var lastResult = null;
    var runId = 0;
    var activePasteController = null;
    var workflow = {
      exampleActive: true,
      revision: 0,
      completionMode: 'guided',
      analysisPending: false,
      capturePending: false,
      captured: false
    };
    var rubricCopy = readRubricCopy(root);
    var saveLabel = saveGuided ? saveGuided.textContent : 'Save this result';
    var exampleValues = {};
    Object.keys(field).forEach(function (key) {
      if (field[key] && key !== 'paste') exampleValues[key] = field[key].value;
    });
    // Per-lever sentences the endpoint wrote about the visitor's own copy.
    // Dropped the moment they edit an answer, because they were about the paste.
    var aiNotes = null;

    function readGuided() {
      return {
        promise: field.promise ? field.promise.value : '',
        price: field.price ? field.price.value : '',
        includes: field.includes ? field.includes.value : '',
        risk: field.risk ? field.risk.value : 'none',
        proof: field.proof ? field.proof.value : 'none',
        why: field.why ? field.why.value : '',
        urgency: field.urgency ? field.urgency.value : 'none'
      };
    }

    function renderStack(result) {
      if (!out.stack) return;
      var stack = result.stack;
      if (!stack || !stack.comparable) {
        out.stack.hidden = true;
        return;
      }
      out.stack.hidden = false;
      out.stack.innerHTML =
        '<span class="sv">' + escapeHtml(formatMoney(stack.value, stack.currency)) + '</span>' +
        '<span class="sx">of value against</span>' +
        '<span class="sp">' + escapeHtml(formatMoney(stack.price, stack.currency)) + '</span>' +
        '<span class="sr">' + (Math.round(stack.ratio * 10) / 10) + 'x</span>';
    }

    function renderRubric(result) {
      if (!rubricCard || !out.levers) return;
      var html = '';
      result.levers.forEach(function (lever) {
        var tone = toneFor(lever.score);
        var state = rubricStateFor(tone);
        var guidance = rubricCopy[lever.key] || {};
        var isPriority = lever.key === result.weakest.key;
        var noteLabel = tone === 'strong' ? 'What’s working.' : 'Why it scored here.';
        var fixLabel = tone === 'leaky' ? 'Next improvement.' : 'Start here.';
        html +=
          '<details class="lev s-' + tone + '" data-bd-lever="' +
          escapeHtml(lever.key) + '"' + (isPriority ? ' open' : '') + '>' +
            '<summary>' +
              '<span class="lh">' +
                '<span class="lid">' +
                  '<span class="ln">' + escapeHtml(lever.name) + '</span>' +
                  '<span class="lstate">' + escapeHtml(state) + '</span>' +
                '</span>' +
                '<span class="lv">' + lever.score + '<i>/100</i></span>' +
                '<span class="lchev" aria-hidden="true">+</span>' +
              '</span>' +
              '<span class="bar" aria-hidden="true"><i style="width:' +
                lever.score + '%"></i></span>' +
            '</summary>' +
            '<div class="ldetail">' +
              '<p class="lnote"><b>' + noteLabel + '</b> ' +
                escapeHtml(lever.note) + '</p>' +
              (tone !== 'strong' && guidance.fix
                ? '<p class="lfix"><b>' + fixLabel + '</b> ' +
                  escapeHtml(guidance.fix) + '</p>'
                : '') +
              (tone === 'broken' && guidance.example
                ? '<p class="lex"><b>Example.</b> ' +
                  escapeHtml(guidance.example) + '</p>'
                : '') +
            '</div>' +
          '</details>';
      });
      out.levers.innerHTML = html;
    }

    function render(result, isExample) {
      lastResult = result;

      if (out.grade) out.grade.textContent = result.grade;
      if (out.score) out.score.innerHTML = result.score + '<small>/100</small>';
      if (out.band) {
        out.band.className = 'o-band ' + result.band;
        out.band.textContent = result.bandLabel;
      }
      if (out.example) out.example.style.display = isExample ? '' : 'none';

      if (out.weakName) out.weakName.textContent = result.weakest.name;
      if (out.weakNote) out.weakNote.textContent = result.weakest.note;
      if (out.more) {
        if (result.moreCount > 0) {
          out.more.hidden = false;
          out.more.textContent = result.moreCount + ' more lever' +
            (result.moreCount === 1 ? ' needs' : 's need') + ' work.';
        } else {
          out.more.hidden = true;
        }
      }

      renderStack(result);
      renderRubric(result);

      if (out.cta) {
        if (result.weakest.key === 'value' || result.weakest.key === 'promise') {
          out.cta.textContent = root.dataset.quizCtaLabel || 'Take the Bottleneck Quiz';
          out.cta.setAttribute('href', root.dataset.quizUrl || '#');
        } else {
          out.cta.textContent = root.dataset.conversionCtaLabel || 'Open the Conversion Rate Calculator';
          out.cta.setAttribute('href', root.dataset.conversionUrl || '#');
        }
      }
    }

    function renderGuided(isExample) {
      render(computeGuided(readGuided(), weights, aiNotes, aiNotes ? 'ai' : 'guided'), isExample);
    }

    /** Show the visitor what we read, in the fields they can edit. */
    function applyFields(fields) {
      Object.keys(fields).forEach(function (key) {
        var node = field[key];
        if (node) node.value = fields[key];
      });
    }

    function clearExampleFields() {
      Object.keys(field).forEach(function (key) {
        var node = field[key];
        if (!node || key === 'paste') return;
        node.value = node.tagName === 'SELECT' ? 'none' : '';
      });
    }

    function beginGuidedEdit(key, nextValue) {
      if (!workflow.exampleActive) return;
      workflow.exampleActive = false;
      workflow.completionMode = 'guided';
      clearExampleFields();
      if (field[key]) field[key].value = nextValue;
    }

    function setStatus(message, tone) {
      if (!out.status) return;
      out.status.hidden = !message;
      out.status.textContent = message || '';
      out.status.className = 'o-status' + (tone ? ' is-' + tone : '');
    }

    function setSaveState(state) {
      if (!saveGuided) return;
      saveGuided.dataset.saveState = state;
      saveGuided.disabled = state !== 'ready';
      if (state === 'saved') {
        saveGuided.textContent = root.dataset.guidedSavedLabel || 'Saved';
      } else if (state === 'stale') {
        saveGuided.textContent = 'Previous result saved';
      } else {
        saveGuided.textContent = saveLabel;
      }
    }

    function setAuditLocked(locked) {
      Object.keys(field).forEach(function (key) {
        if (field[key]) field[key].disabled = locked;
      });
      [tabGuided, tabPaste, runPaste].forEach(function (control) {
        if (control) control.disabled = locked;
      });
    }

    // This is the only writer for result revisions. It keeps the rendered
    // result, save button and account-status copy on the same revision.
    function markResultChanged(message) {
      workflow.revision += 1;
      if (workflow.captured) {
        setSaveState('stale');
        setStatus(
          (message ? message + ' ' : '') +
          'Your previous result was saved. This updated grade is not saved.',
          ''
        );
      } else if (!workflow.capturePending) {
        setSaveState('ready');
        setStatus(message || '', '');
      }
    }

    /**
     * Report the outcome so it becomes a tag on the Shopify customer, which is
     * what Shopify Flow watches. Scoring happens in the browser, so the server
     * cannot know the grade unless we tell it; it whitelists whatever we send
     * before any of it becomes a tag.
     *
     * Only fires after the signed-in visitor explicitly confirms the result,
     * and only once per visit. A result is not called saved until the endpoint
     * confirms the request with a successful HTTP response.
     */
    function capture(result, mode) {
      if (workflow.captured || workflow.capturePending || !result || !captureEndpoint || !isLoggedIn) {
        return Promise.resolve('ignored');
      }

      var submittedRevision = workflow.revision;
      workflow.capturePending = true;
      setAuditLocked(true);
      setSaveState('saving');

      var controller = typeof global.AbortController !== 'undefined'
        ? new global.AbortController()
        : null;
      var request;
      try {
        request = global.fetch(captureEndpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            tool: 'offerAudit',
            grade: result.grade,
            weakest: result.weakest.key
          }),
          keepalive: true,
          signal: controller ? controller.signal : undefined
        });
      } catch (_error) {
        request = Promise.reject(_error);
      }

      var timeout;
      var timedRequest = Promise.race([
        Promise.resolve(request),
        new Promise(function (_resolve, reject) {
          timeout = global.setTimeout(function () {
            if (controller) controller.abort();
            reject(new Error('capture_timeout'));
          }, 30000);
        })
      ]);

      return timedRequest.then(function (response) {
        if (timeout) global.clearTimeout(timeout);
        workflow.capturePending = false;
        if (!response || !response.ok) {
          setAuditLocked(false);
          setSaveState('ready');
          return 'failed';
        }
        if (workflow.revision !== submittedRevision) {
          setAuditLocked(false);
          setSaveState('ready');
          return 'changed';
        }

        workflow.captured = true;
        setAuditLocked(false);
        setSaveState('saved');
        track('bd_offer_audit_completed', {
          mode: mode,
          grade: result.grade,
          score: result.score,
          weakest: result.weakest.key
        });
        return 'saved';
      }, function () {
        if (timeout) global.clearTimeout(timeout);
        workflow.capturePending = false;
        setAuditLocked(false);
        setSaveState('ready');
        return 'failed';
      });
    }

    function cancelPasteAnalysis() {
      runId += 1;
      workflow.analysisPending = false;
      if (activePasteController) {
        activePasteController.abort();
        activePasteController = null;
      }
      if (runPaste && !workflow.capturePending) runPaste.disabled = false;
    }

    async function runPasteAnalysis() {
      var offerText = field.paste ? text(field.paste.value) : '';
      if (words(offerText).length < 12) {
        setStatus('Paste a bit more of the offer, the headline, the price and what is included, and we will read it properly.', 'error');
        if (field.paste) field.paste.focus();
        return;
      }
      if (offerText.length > OFFER_TEXT_MAX_LENGTH) {
        setStatus('This reader can take up to 8,000 characters at a time. Shorten the pasted offer so we can grade the complete copy.', 'error');
        if (field.paste) field.paste.focus();
        return;
      }

      cancelPasteAnalysis();
      var currentRun = ++runId;
      workflow.analysisPending = true;
      setStatus('Reading your offer…', 'busy');
      if (runPaste) runPaste.disabled = true;
      track('bd_offer_audit_started', { mode: 'paste' });

      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      activePasteController = controller;
      var timeout = controller && global.setTimeout(function () { controller.abort(); }, 30000);

      try {
        var response = await global.fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            offerText: offerText
          }),
          signal: controller ? controller.signal : undefined
        });

        var payload;
        try {
          payload = await response.json();
        } catch (_error) {
          throw new Error('The offer reader is not connected yet. Answer the questions instead and you will get the same rubric.');
        }
        if (currentRun !== runId) return;
        if (!response.ok || payload.ok === false) {
          throw new Error(payload.message || 'We could not read that offer just now. Please try again shortly.');
        }

        var fields = readFields(payload);
        if (!fields) {
          throw new Error('We could not make sense of that offer. Answer the questions instead and you will get the same rubric.');
        }

        // The endpoint reads; it never scores. Its answers land in the guided
        // fields and the same browser scorer runs on them, so a paste and a
        // typed answer can never produce different grades. Showing the fields
        // also means a misread is a two-second edit, not a wrong verdict.
        aiNotes = readNotes(payload);
        workflow.exampleActive = false;
        workflow.completionMode = 'paste';
        applyFields(fields);
        selectTab('guided');
        markResultChanged(
          root.dataset.readNote ||
          'Here is what we read. Correct anything we got wrong and the grade updates.'
        );
      } catch (error) {
        if (currentRun !== runId) return;
        var message = error && error.name === 'AbortError'
          ? 'That took too long. Answer the questions instead and you will get the same rubric.'
          : error.message;
        setStatus(message, 'error');
        track('bd_offer_audit_error', { mode: 'paste' });
      } finally {
        if (timeout) global.clearTimeout(timeout);
        if (activePasteController === controller) activePasteController = null;
        if (currentRun === runId) {
          workflow.analysisPending = false;
          if (runPaste) runPaste.disabled = false;
        }
      }
    }

    function selectTab(which) {
      var guided = which === 'guided';
      if (tabGuided) tabGuided.setAttribute('aria-selected', guided ? 'true' : 'false');
      if (tabPaste) tabPaste.setAttribute('aria-selected', guided ? 'false' : 'true');
      if (paneGuided) paneGuided.hidden = !guided;
      if (panePaste) panePaste.hidden = guided;
      setStatus('', '');

      if (guided) {
        if (gateCard) gateCard.hidden = true;
        if (resultCard) resultCard.hidden = false;
        renderGuided(workflow.exampleActive);
      } else if (!isLoggedIn && gateCard) {
        // The paste path costs us a model call, so it sits behind the account.
        if (resultCard) resultCard.hidden = true;
        gateCard.hidden = false;
      }
    }

    if (tabGuided) {
      tabGuided.addEventListener('click', function () {
        cancelPasteAnalysis();
        selectTab('guided');
      });
    }
    if (tabPaste) {
      tabPaste.addEventListener('click', function () {
        cancelPasteAnalysis();
        selectTab('paste');
      });
    }

    if (runPaste) {
      runPaste.addEventListener('click', function () {
        if (!isLoggedIn) {
          if (resultCard) resultCard.hidden = true;
          if (gateCard) gateCard.hidden = false;
          return;
        }
        runPasteAnalysis();
      });
    }

    if (field.paste) {
      field.paste.addEventListener('input', function () {
        if (!workflow.analysisPending) return;
        cancelPasteAnalysis();
        setStatus('The pasted offer changed. Run it again when you are ready.', '');
      });
    }

    toGuidedButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        cancelPasteAnalysis();
        selectTab('guided');
      });
    });

    registerButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        var registerUrl = root.dataset.registerUrl;
        track('bd_offer_audit_gate_click', {});
        if (registerUrl) global.location.href = registerUrl;
      });
    });

    Object.keys(field).forEach(function (key) {
      var node = field[key];
      if (!node || key === 'paste') return;
      if (node.tagName !== 'SELECT') {
        node.addEventListener('beforeinput', function () {
          beginGuidedEdit(key, '');
        });
      }
      var handler = function () {
        cancelPasteAnalysis();
        if (workflow.exampleActive) {
          var nextValue = node.value;
          var exampleValue = exampleValues[key] || '';
          if (node.tagName !== 'SELECT' && exampleValue) {
            nextValue = nextValue.replace(exampleValue, '');
          }
          beginGuidedEdit(key, nextValue);
        }
        // Once they edit an answer, the notes written about the pasted copy no
        // longer describe what is on screen, so they go.
        if (aiNotes) {
          aiNotes = null;
        }
        markResultChanged('');
        renderGuided(false);
      };
      node.addEventListener('input', handler);
      node.addEventListener('change', handler);
    });

    if (saveGuided) {
      saveGuided.addEventListener('click', function () {
        if (workflow.exampleActive || !lastResult || workflow.captured || workflow.capturePending) return;
        if (!captureEndpoint) return;
        setStatus('Saving your grade and weakest lever…', 'busy');
        capture(lastResult, workflow.completionMode).then(function (outcome) {
          if (outcome === 'saved') {
            setStatus('Saved. Your grade and weakest lever are now on your account.', '');
          } else if (outcome === 'changed') {
            setStatus('This result changed before the save finished. Review it and save again.', 'error');
          } else if (outcome === 'failed') {
            setStatus('We could not save that result. Check your connection and try again.', 'error');
          }
        });
      });
      setSaveState('locked');
      if (!captureEndpoint) {
        var guidedNote = root.querySelector('[data-bd-guided-note]');
        saveGuided.hidden = true;
        if (guidedNote) {
          guidedNote.textContent = root.dataset.guidedLocalNote ||
            '// nothing is sent. the scoring runs in your browser.';
        }
      }
    }

    // First paint shows the worked example that ships with the section.
    renderGuided(true);
  }

  function boot() {
    queryAll(global.document, '[data-bd-offer-audit]').forEach(init);
  }

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }

  // Exposed so the scoring can be tested without a browser.
  global.BDOfferAudit = {
    LEVERS: LEVERS,
    computeGuided: computeGuided,
    readFields: readFields,
    readNotes: readNotes,
    weightsFrom: weightsFrom
  };
})(typeof window !== 'undefined' ? window : this);
